import { useEffect, useRef, useState } from 'react';

/**
 * A custom hook that detects when an element enters the viewport
 * @param {Object} options - IntersectionObserver options
 * @param {number} options.threshold - A number between 0 and 1 indicating the percentage that should be visible before triggering
 * @param {string} options.root - The element that is used as the viewport for checking visibility (defaults to browser viewport)
 * @param {string} options.rootMargin - Margin around the root element
 * @returns {Array} [ref, isVisible] - The reference to attach to the element and whether it's visible
 */
const useScrollAnimation = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // If the element ref isn't assigned yet, return early
    if (!ref.current) return;

    // Save a copy of the current value
    const currentElement = ref.current;

    const observer = new IntersectionObserver(([entry]) => {
      // Update our state when the component enters the viewport
      if (entry.isIntersecting) {
        setIsVisible(true);
        // Once the element is visible, we can stop observing it
        observer.unobserve(currentElement);
      }
    }, {
      threshold: options.threshold || 0.1,
      root: options.root || null,
      rootMargin: options.rootMargin || '0px',
    });

    observer.observe(currentElement);

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [options.threshold, options.root, options.rootMargin]);

  return [ref, isVisible];
};

export default useScrollAnimation; 